// Original educational guides written for Regal Jewels
export const GUIDES = [
  {
    slug: "gold-purity-guide",
    title: "Gold Purity Explained: 10K, 14K, 18K, 22K & 24K",
    summary: "What karat numbers really mean, how purity affects color, durability and price, and which karat suits everyday wear versus bridal jewelry.",
    minutes: 5,
    sections: [
      {
        h: "What does karat mean?",
        p: "Karat (K) measures how much pure gold is in a piece of jewelry, out of 24 parts. 24K is essentially pure gold (99.9%). 22K contains about 91.6% gold, 18K about 75%, 14K about 58.3%, and 10K about 41.7%. The remaining metal is an alloy — usually copper, silver or zinc — added for strength and color.",
      },
      {
        h: "How purity changes the jewelry",
        p: "Higher-karat gold has a deeper, warmer yellow tone and greater intrinsic value, but it is softer. 22K is the traditional choice for South Asian bridal sets, bangles and chains because of its rich color and strong resale value. 18K balances luxury with durability and is common in diamond settings, since the firmer alloy holds stones securely. 14K is the most scratch-resistant choice for daily-wear rings and bracelets.",
      },
      {
        h: "Which karat should you choose?",
        p: "For heirloom and bridal pieces where gold value matters most, choose 22K. For diamond jewelry and engagement rings, 18K or 14K settings protect the stones better. For investment, 24K bullion coins and bars carry the highest purity. At Regal Jewels in Chicago we carry 18K, 22K and 24K gold, and our team can weigh and explain any piece before you buy.",
      },
      {
        h: "How gold jewelry is priced",
        p: "Gold jewelry pricing is based on the day's gold rate multiplied by the item's weight in grams, plus craftsmanship. That is why identical-looking chains can differ in price — the weight and purity differ. Always ask for the gram weight and karat, which we list on every product.",
      },
    ],
    related: [
      { label: "Shop 22K Gold", to: "/collections/gold" },
      { label: "Shop Gold Bullion", to: "/collections/bullion" },
    ],
  },
  {
    slug: "diamond-4cs-guide",
    title: "The 4Cs of Diamonds: Cut, Color, Clarity & Carat",
    summary: "A plain-English walkthrough of the four factors that determine a diamond's beauty and price — and where you can safely save money.",
    minutes: 6,
    sections: [
      {
        h: "Cut — the sparkle factor",
        p: "Cut describes how well a diamond's facets interact with light, and it has the biggest impact on sparkle. A well-cut diamond looks brighter and can even appear larger than a poorly cut stone of the same weight. If you prioritize one C, make it cut.",
      },
      {
        h: "Color — the whiteness scale",
        p: "Diamond color is graded from D (colorless) to Z (noticeably tinted). To the naked eye, stones in the G–I range usually look white once set in jewelry, especially in yellow gold where a slight warmth blends in. Paying for D–F color matters most in white-metal settings.",
      },
      {
        h: "Clarity — natural fingerprints",
        p: "Clarity measures internal inclusions and surface blemishes, from FL (flawless) to I3 (included). Most inclusions in VS and SI grades are invisible without magnification. An eye-clean SI1 diamond can cost significantly less than a VVS stone that looks identical on the hand.",
      },
      {
        h: "Carat — weight, not size",
        p: "Carat is weight: 1 carat equals 0.2 grams. Two diamonds of equal carat can face up differently depending on cut proportions. Popular weights like 1.00ct carry price premiums — a 0.90ct stone often looks nearly the same for noticeably less.",
      },
      {
        h: "Certification",
        p: "Ask for a grading report from an independent laboratory when buying an important diamond. Visit us in Chicago to compare certified diamonds side by side — seeing stones in person is the fastest way to understand the 4Cs.",
      },
    ],
    related: [
      { label: "Shop Diamond Jewelry", to: "/collections/diamond" },
      { label: "Book a Diamond Consultation", to: "/book" },
    ],
  },
  {
    slug: "ring-size-guide",
    title: "Ring Size Guide: Find Your Perfect Fit",
    summary: "Simple at-home methods to measure your ring size, US size conversions, and tips for surprise proposals.",
    minutes: 4,
    sections: [
      {
        h: "Measure with a ring you own",
        p: "The most accurate home method is measuring the inside diameter of a ring that already fits the intended finger. Place it on a ruler and measure edge to edge inside the band in millimeters. For example, about 16.5mm corresponds to US size 6, 17.3mm to size 7, and 18.1mm to size 8.",
      },
      {
        h: "The string method",
        p: "Wrap a strip of paper or non-stretch string snugly around the base of the finger, mark where it overlaps, and measure the length in millimeters — that is the circumference. Divide by 3.14 to estimate the diameter. Measure at the end of the day when fingers are warm, since cold fingers shrink slightly.",
      },
      {
        h: "Fit tips",
        p: "A correctly sized ring slides on smoothly and resists slightly over the knuckle when removed. Wide bands fit tighter, so consider going up a quarter to half size for bands over 6mm. If you are between sizes, size up — rings can usually be resized down more easily.",
      },
      {
        h: "Buying a surprise ring?",
        p: "Borrow a ring your partner wears on the correct finger and trace its inside circle on paper, or ask us — at our Devon Avenue store we can size any ring on the spot, and most of our rings can be adjusted after the proposal.",
      },
    ],
    related: [
      { label: "Shop Ladies Rings", to: "/shop?type=rings&audience=women" },
      { label: "Shop Men's Rings", to: "/shop?type=rings&audience=men" },
    ],
  },
  {
    slug: "jewelry-care-guide",
    title: "How to Care for Gold & Diamond Jewelry",
    summary: "Everyday habits, safe cleaning methods at home, and what to avoid so your gold and diamonds stay brilliant for generations.",
    minutes: 5,
    sections: [
      {
        h: "Everyday habits that protect jewelry",
        p: "Put jewelry on last — after lotion, perfume and hairspray — and take it off first. Remove rings before workouts, swimming, cleaning and cooking. Chlorine and household bleach can permanently damage gold alloys, and hard knocks can loosen stone settings.",
      },
      {
        h: "Cleaning gold at home",
        p: "Soak gold jewelry in warm water with a few drops of mild dish soap for 10–15 minutes, brush gently with a soft toothbrush, rinse and pat dry with a lint-free cloth. Avoid toothpaste, baking soda and abrasive cloths, which create fine scratches, especially on high-purity 22K gold.",
      },
      {
        h: "Caring for diamond jewelry",
        p: "Diamonds attract oils, which dull their sparkle. Clean them the same gentle way every few weeks, and check prongs occasionally by pushing the stone lightly — if it moves or rattles, stop wearing it and have the setting tightened.",
      },
      {
        h: "Storage and professional care",
        p: "Store each piece separately in soft pouches so diamonds do not scratch gold. Have clasps, prongs and chains professionally inspected about once a year. We offer cleaning and repair services at our Chicago store — bring your pieces by any day of the week.",
      },
    ],
    related: [
      { label: "Visit Our Store", to: "/contact" },
      { label: "Shop All Jewelry", to: "/shop" },
    ],
  },
  {
    slug: "buying-22k-gold-guide",
    title: "Buying 22K Gold Jewelry: Weight, Rates & What to Ask",
    summary: "How 22kt gold jewelry is priced, why gram weight matters, and the questions smart buyers ask before purchasing.",
    minutes: 5,
    sections: [
      {
        h: "Why 22K is the bridal standard",
        p: "22K gold (91.6% pure) is the traditional standard for South Asian wedding and heirloom jewelry. Its rich warm color cannot be replicated by lower karats, and its high gold content means the piece holds intrinsic value you can verify by weight at any time.",
      },
      {
        h: "Understanding weight-based pricing",
        p: "22K jewelry is priced primarily by gram weight multiplied by the prevailing gold rate, plus making charges for craftsmanship. That is why we publish the gram weight on our products — for example, a 20-inch chain at 33.77 grams is priced very differently from a similar-looking 10-gram chain.",
      },
      {
        h: "Questions to ask before you buy",
        p: "Ask for the exact karat and gram weight, whether stones or enamel (minakari) are included in the weight, the current gold rate being applied, and the store's exchange or upgrade policy. A trustworthy jeweler answers all four without hesitation — and we always do.",
      },
      {
        h: "Buying from anywhere in the USA",
        p: "If you are not in Chicago, we photograph pieces on request, confirm weights over WhatsApp, and ship fully insured across the USA. You can also book a private appointment at our Devon Avenue showroom to see collections in person.",
      },
    ],
    related: [
      { label: "Shop 22K Gold", to: "/collections/gold" },
      { label: "WhatsApp Us a Question", to: "/contact" },
    ],
  },
  {
    slug: "bangle-size-guide",
    title: "Bangle Size Guide: Measuring for Indian Gold Bangles",
    summary: "How traditional bangle sizes like 2-4, 2-6 and 2-8 work, and two easy ways to find your size at home.",
    minutes: 4,
    sections: [
      {
        h: "How Indian bangle sizes work",
        p: "Traditional bangle sizes describe the inner diameter in inches: size 2-4 means 2 and 4/16 inches (2.25″), 2-6 means 2 and 6/16 inches (2.375″), and 2-8 means 2.5 inches. Most adult women wear between 2-4 and 2-8, but hand shape matters as much as wrist size.",
      },
      {
        h: "Method 1: measure a bangle you own",
        p: "Measure the inside diameter of a bangle that slides on comfortably, straight across the widest inner point. Match that measurement to the size chart — 2.25″ = 2-4, 2.3125″ = 2-5, 2.375″ = 2-6, 2.4375″ = 2-7, 2.5″ = 2-8.",
      },
      {
        h: "Method 2: measure your hand",
        p: "Tuck your thumb toward your palm as if putting on a bangle, and measure around the widest part of your hand (across the knuckles and thumb joint) with a flexible tape. Divide the circumference by 3.14 to get the diameter, then choose the closest size up.",
      },
      {
        h: "Fit preferences",
        p: "A classic fit slides over the hand with gentle pressure and rests loosely on the wrist. For daily-wear single bangles some prefer a snugger fit; for bridal sets of 6 or 12 bangles, consistent sizing matters most. Visit us in Chicago to try sizes in person — it takes two minutes to know for sure.",
      },
    ],
    related: [
      { label: "Shop Gold Bangles", to: "/shop?type=bangles" },
      { label: "Book a Try-On Visit", to: "/book" },
    ],
  },
];

export function getGuide(slug) {
  return GUIDES.find((g) => g.slug === slug);
}
